import { create } from 'zustand';
import { getSessionDetail } from '@/api/sessions';
import { currentPushClock, useConversationStore } from '@/stores/conversationStore';
import { mapSessionToConversation } from '@/utils/mapSession';
import type { SessionDto } from '@/types';
import { canSee } from './folders';
import { httpStatus, readViewer, scopeKey } from './viewer';

/**
 * ADR-033 / PR-C2 - the two small pieces the console's re-reads share: WHICH rows are being re-read right now, and the
 * rule that decides whether an answer may still be applied - plus `createRowRefresher`, the ONE way the console
 * re-reads a single conversation.
 *
 * <b>Latest request wins.</b> Every list page, row re-read, counts read and catalog read takes a sequence number when it
 * is SENT, per target (`folder:mine`, `row:<id>`, `counts`, `catalog`). When an answer lands, it is applied only if no
 * answer with a HIGHER number has already been applied for that target; an older answer arriving after a newer one is
 * dropped. Without this an older list response landing after a newer one overwrites owner/state fields the newer one had
 * already corrected - the exact way a stale row regains an owner it has lost (PR-C1 review residual). Arrival order is
 * not request order on a network, and an answer that was in flight when a push (or a newer re-read) corrected the row
 * is, by construction, older than what the console already knows.
 */
export interface Sequencer {
  /** Take the next number for `target`, at the moment the request is sent. */
  next: (target: string) => number;
  /** May the answer numbered `n` for `target` be applied? True (and recorded) unless a newer answer was already applied. */
  accept: (target: string, n: number) => boolean;
  /**
   * Is the request numbered `n` still the newest answer the target has had? Read-only: a FAILURE is not an answer, so it
   * must not mark newer-than-this as applied (that would throw away an older request's good answer) - it only asks
   * whether it is allowed to speak for the target.
   */
  isCurrent: (target: string, n: number) => boolean;
  /** Forget every target (the signed-in tenant+agent changed: nothing from before may be compared with what follows). */
  reset: () => void;
}

export function createSequencer(): Sequencer {
  let counter = 0;
  const applied = new Map<string, number>();
  return {
    next: () => ++counter,
    accept: (target, n) => {
      if (n < (applied.get(target) ?? 0)) return false;
      applied.set(target, n);
      return true;
    },
    isCurrent: (target, n) => n >= (applied.get(target) ?? 0),
    reset: () => {
      applied.clear();
    },
  };
}

/**
 * How a `refreshRow` ended - what the caller may conclude from it.
 *  - `applied`    the server answered with this row and the console now holds it (200).
 *  - `superseded` a NEWER re-read of the same row had already been applied, so this answer was dropped; the row is at
 *                 least as fresh as this request would have made it. (An answer a PUSH overtook is not this: it is
 *                 asked again, and ends `failed` if the row keeps moving.)
 *  - `hidden`     the server will not show the row to this viewer (404), or a push hid it while the read was in flight,
 *                 or the row it returned is one this viewer must not see: the row is in the hidden set.
 *  - `failed`     any other failure (network, 5xx, a malformed answer): the row is exactly as it was. NEVER evidence.
 */
export type RowRefreshOutcome = 'applied' | 'superseded' | 'hidden' | 'failed';

interface RowRefreshState {
  /** sessionId -> how many re-reads of that row are queued or in flight. Absent = none. */
  inflight: Record<string, number>;
  begin: (sessionId: string) => void;
  end: (sessionId: string) => void;
  /** Forget everything (tenant+agent changed). */
  clear: () => void;
}

/**
 * Which rows are being re-read RIGHT NOW (queued or in flight). The owner of a human-controlled conversation is not
 * trusted while one is: that is the whole of the "Confirming who owns this conversation" state
 * (console/useVerifiedOwner.ts). It is a store, not a ref, so every reader (chat view, contact panel) re-renders the
 * moment it starts and the moment it ends, with no frame in between that trusts a row about to be replaced.
 */
export const useRowRefreshStore = create<RowRefreshState>((set) => ({
  inflight: {},
  begin: (sessionId) =>
    set((s) => ({ inflight: { ...s.inflight, [sessionId]: (s.inflight[sessionId] ?? 0) + 1 } })),
  end: (sessionId) =>
    set((s) => {
      const n = s.inflight[sessionId] ?? 0;
      if (n <= 0) return s;
      const next = { ...s.inflight };
      if (n === 1) delete next[sessionId];
      else next[sessionId] = n - 1;
      return { inflight: next };
    }),
  clear: () => set({ inflight: {} }),
}));

/** Rows that ask to be re-read within this window share one batch. */
export const ROW_BATCH_MS = 300;
/** At most this many `GET /api/sessions/{id}` in flight at once. */
export const ROW_CONCURRENCY = 4;
/** How many times a read refused as stale (a push overtook it) is asked again before it ends as `failed`. */
export const MAX_STALE_RETRIES = 2;

type RowWaiter = (outcome: RowRefreshOutcome) => void;

export interface RowRefresher {
  /**
   * Re-read ONE conversation from the server and apply the answer. Ids asked for within the batch window share one
   * request (a burst of pushes for one row is one read), at most `concurrency` reads run at once. The row is marked "being
   * re-read" (`useRowRefreshStore`) from THIS call until the answer is applied. Resolves with how it ended; never
   * rejects. Everything about the outcome is the SERVER's word: nothing here guesses an owner.
   *  - 200: the row is replaced (console-only decoration kept) and un-hidden - unless the viewer may not see it, or a
   *    push hid it after this read was sent;
   *  - 404: the row is hidden (reversible);
   *  - anything else: the row is left exactly as it was.
   */
  refreshRow: (sessionId: string) => Promise<RowRefreshOutcome>;
  /** Stop the timer and settle everything still waiting as `failed`, so nothing is left "being re-read" for ever (the queue stays usable). */
  dispose: () => void;
}

/**
 * The row-refresh queue behind `ConsoleContext.refreshRow`. `batchMs <= 0` flushes at once (tests, and nothing else).
 * Not a hook: it owns plain state, so the shell holds one in a ref and tests can build their own.
 */
export function createRowRefresher(opts: { seq: Sequencer; batchMs?: number; concurrency?: number }): RowRefresher {
  const { seq } = opts;
  const batchMs = opts.batchMs ?? ROW_BATCH_MS;
  const concurrency = opts.concurrency ?? ROW_CONCURRENCY;

  const pending = new Map<string, RowWaiter[]>();
  let ready: Array<{ id: string; waiters: RowWaiter[] }> = [];
  let active = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;

  /** Apply a row the server just returned: only if this viewer may see it, and through the store's single-row gate. */
  const applyRow = (id: string, dto: SessionDto, requestedAt: number, sentAtClock: number): RowRefreshOutcome | 'stale' => {
    const store = useConversationStore.getState();
    const row = mapSessionToConversation(dto);
    const viewer = readViewer();
    // Defence in depth: under private ownership the server does not send a row this viewer may not see. If one arrives
    // anyway (or its owner is not stated), hide it rather than show it.
    if (!canSee(row, viewer.me, viewer.roles, row.ownershipEnforced)) {
      store.hideSession(id);
      return 'hidden';
    }
    // `stale`: a state / owner / consultation / category / name push moved the row AFTER this read was sent, so the answer
    // cannot have seen it and was refused rather than rolling the push back.
    return store.applyRefreshedRow(row, requestedAt, sentAtClock);
  };

  /** One GET and its verdict. `stale` = a push moved the row after this GET was sent, so the answer was refused. */
  const readOnce = async (id: string): Promise<RowRefreshOutcome | 'stale'> => {
    const scope = scopeKey();
    const requestedAt = Date.now();
    const sentAtClock = currentPushClock();
    const target = `row:${id}`;
    const n = seq.next(target);
    try {
      const dto = await getSessionDetail(id);
      if (scope !== scopeKey()) return 'failed';
      // May this answer speak for the row? A READ-ONLY question: a newer answer has already been applied -> this one is
      // older news. The number is recorded only AFTER the store has actually applied the answer (below): a read the store
      // refuses as stale (a push overtook it) or as hidden said nothing about the row, so it must not be recorded as
      // applied - otherwise an OLDER read that lands afterwards would be called `superseded` by something that never
      // landed, and a caller that treats `superseded` as "a newer read already confirmed the row" would trust a stale owner.
      if (!seq.isCurrent(target, n)) return 'superseded';
      const outcome = applyRow(id, dto, requestedAt, sentAtClock);
      if (outcome === 'applied') seq.accept(target, n);
      return outcome;
    } catch (err) {
      if (scope !== scopeKey()) return 'failed';
      if (httpStatus(err) === 404) {
        // The server will not show this row to this viewer (or it is gone): hide it, reversibly. A 404 older than a
        // newer answer that was already applied is just old news.
        if (!seq.accept(target, n)) return 'superseded';
        useConversationStore.getState().hideSession(id);
        return 'hidden';
      }
      return 'failed';
    }
  };

  const runRead = async (id: string, waiters: RowWaiter[]): Promise<void> => {
    let outcome: RowRefreshOutcome = 'failed';
    try {
      // A push that moved the row (state, owner, consultation, category, name) after the GET was sent makes its answer
      // unusable - applying it would roll the push back. The row still needs the SERVER's word, so ask again, now that the
      // push has landed; a row that keeps moving under us ends as `failed` (unknown - never "verified").
      for (let attempt = 0; attempt <= MAX_STALE_RETRIES; attempt += 1) {
        const result = await readOnce(id);
        if (result !== 'stale') {
          outcome = result;
          break;
        }
      }
    } finally {
      const refreshing = useRowRefreshStore.getState();
      for (const w of waiters) {
        refreshing.end(id);
        w(outcome);
      }
    }
  };

  const pump = () => {
    while (active < concurrency && ready.length > 0) {
      const job = ready.shift()!;
      active += 1;
      void runRead(job.id, job.waiters).finally(() => {
        active -= 1;
        pump();
      });
    }
  };

  const flush = () => {
    timer = null;
    for (const [id, waiters] of pending) ready.push({ id, waiters });
    pending.clear();
    pump();
  };

  const refreshRow = (sessionId: string): Promise<RowRefreshOutcome> => {
    if (!sessionId) return Promise.resolve('failed');
    useRowRefreshStore.getState().begin(sessionId);
    return new Promise<RowRefreshOutcome>((resolve) => {
      const list = pending.get(sessionId);
      if (list) list.push(resolve);
      else pending.set(sessionId, [resolve]);
      if (batchMs <= 0) flush();
      else if (timer === null) timer = setTimeout(flush, batchMs);
    });
  };

  const dispose = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    const refreshing = useRowRefreshStore.getState();
    for (const [id, waiters] of pending) {
      for (const w of waiters) {
        refreshing.end(id);
        w('failed');
      }
    }
    pending.clear();
    for (const job of ready) {
      for (const w of job.waiters) {
        refreshing.end(job.id);
        w('failed');
      }
    }
    ready = [];
  };

  return { refreshRow, dispose };
}
